import { Dayjs } from 'dayjs'
import { ComponentProps, forwardRef } from 'react'

import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons'
import { styled } from '@mui/material'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers'

import { Icon } from '../Icon/Icon'
import { IconButton } from '../IconButton/IconButton'
import { TextField } from '../TextField/TextField'

export type DatePickerProps = Omit<ComponentProps<typeof StyledDatePicker>, 'variant' | 'round' | '$round'> & {
  round?: boolean
  highlightedDates?: Dayjs[]
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(({ round, highlightedDates, ...props }, ref) => {
  const isHighlightedDate = (date: Dayjs): boolean => {
    if (!highlightedDates) return false

    return highlightedDates.some(highlightedDate =>
      highlightedDate.format('DD/MM/YYYY') === date.format('DD/MM/YYYY'),
    )
  }

  return (
    <StyledDatePicker
      inputRef={ref}
      $round={round}
      format="DD/MM/YYYY"
      slots={{
        textField: TextField,
        openPickerButton: IconButton,
        openPickerIcon: () => (
          <StyledIcon icon={faCalendarDays}></StyledIcon>
        ),
      }}
      {...props}
      slotProps={{
        ...props?.slotProps,
        day: (dayProps) => {
          const isHighlighted = isHighlightedDate(dayProps.day)

          return {
            ...dayProps,
            sx: {
              ...(isHighlighted && {
                backgroundColor: 'secondary.main',
                color: 'primary.contrastText',
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&.Mui-selected': {
                  backgroundColor: 'primary.dark',
                },
              }),
            },
          }
        },
      }}
    />
  )
})



const StyledDatePicker = styled(MuiDatePicker, {
  shouldForwardProp: (prop) => prop !== '$round',
}) <{ $round: boolean | undefined }>`
  width: 100%;
`

const StyledIcon = styled(Icon)`
  font-size: ${props => props.theme.typography.pxToRem(16)};
  width: ${props => props.theme.typography.pxToRem(16)};
  height: ${props => props.theme.typography.pxToRem(16)};
  color: ${props => props.theme.palette.text.secondary};
`